import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, Check, Loader2, MessageSquareText, Network, RotateCw, Trash2 } from "lucide-react";
import { SaveAsNote } from "@/components/conecta/SaveAsNote";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { invalidateContacts } from "@/lib/lookups";
import { notifyReviewChanged } from "@/lib/useReviewCount";
import { Section } from "@/components/common/DetailFields";
import { BackLink } from "@/components/conecta/Bits";
import { ActionCard } from "@/components/conecta/ActionCard";
import { CaptureMedia } from "@/components/conecta/CaptureMedia";
import { STATUS } from "@/components/conecta/CaptureRow";
import { keyed } from "@/lib/utils";

const LINK = { contact: "/conecta/red/", opportunity: "/conecta/negocios/", investment: "/inversiones/", loan: "/prestamos/", need: "/conecta/necesidades#" };

export default function CaptureCardPage() {
  const { id } = useParams();
  const { isOwner } = useAuth();
  const [cap, setCap] = useState(null);
  const [actions, setActions] = useState([]);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/captures/${id}`).then((r) => {
    setCap(r.data);
    setActions((prev) => (prev.length && r.data.status === "extraida" ? prev : r.data.proposal));
    return r.data;
  }).catch((e) => { setCap(false); toast.error(errMsg(e)); }), [id]);
  useEffect(() => {
    let t;
    const poll = async () => { const c = await load(); if (c && STATUS[c.status]?.busy) t = setTimeout(poll, 2500); };
    poll();
    return () => clearTimeout(t);
  }, [load]);

  const networkNeeds = useMemo(() => actions.filter((a) => a.type === "need" && a.data?.text && !a.data.contact_key), [actions]);
  const contactKeys = useMemo(() => Object.fromEntries(actions.filter((a) => a.type === "contact" && a.enabled).map((a) => [a.key, a.data.name])), [actions]);

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/captures/${id}/apply`, { actions });
      setCap(data.capture);
      setErrors(data.errors);
      invalidateContacts();
      notifyReviewChanged();
      Object.keys(data.errors).length ? toast.error("Algunas acciones no se aplicaron") : toast.success("Aplicado");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const act = async (path, msg) => {
    try { await api.post(`/captures/${id}/${path}`); toast.success(msg); notifyReviewChanged(); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (cap === false) return <div className="space-y-3"><BackLink to="/conecta/revisar" label="Por revisar" /><p data-testid="capture-not-found" className="c-card p-6 text-center c-text-2">Captura no encontrada.</p></div>;
  if (!cap) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin c-muted" /></div>;
  const st = STATUS[cap.status];
  const editable = cap.status === "extraida";

  return (
    <div data-testid="capture-card-page" className="space-y-4">
      <BackLink to="/conecta/revisar" label="Por revisar" />
      <div className="flex items-center justify-between gap-2">
        <span data-testid="capture-status" className="text-sm font-semibold c-accent flex items-center gap-1.5">{st.busy && <Loader2 className="w-4 h-4 animate-spin" />}{st.label}</span>
        {cap.confidence != null && <span data-testid="capture-confidence" className="font-mono text-xs px-2 py-0.5 rounded bg-[var(--c-elev)]">Confianza {Math.round(cap.confidence * 100)}%</span>}
      </div>
      {cap.summary && <p data-testid="capture-summary" className="font-heading text-lg font-bold leading-snug">{cap.summary}</p>}
      <Section title={cap.kind === "photo" ? "Texto de la foto" : "Transcripción"} testid="capture-transcription">
        <CaptureMedia captureId={cap.id} kind={cap.kind} />
        <p data-testid="capture-transcription-text" className="text-sm c-text-2 whitespace-pre-wrap mt-2">{cap.transcription || cap.text_input || "…"}</p>
      </Section>
      {cap.status === "error" && (
        <div className="c-card p-4 space-y-2 !border-red-500/50">
          <p data-testid="capture-error" className="text-sm text-red-500">{cap.error}</p>
          <button data-testid="capture-retry-btn" onClick={() => act("retry", "Reintentando")} className="text-sm font-semibold c-accent inline-flex items-center gap-1"><RotateCw className="w-4 h-4" /> Reintentar</button>
        </div>
      )}
      {cap.review_reasons?.length > 0 && cap.status !== "aplicada" && (
        <div data-testid="capture-review-reasons" className="rounded-xl p-3 text-xs space-y-1 bg-amber-500/10 border border-amber-500/30 text-amber-800 dark:text-amber-200">
          <p className="flex items-center gap-1.5 font-semibold uppercase tracking-wider"><AlertTriangle className="w-3.5 h-3.5" /> Revisa antes de confirmar</p>
          {keyed(cap.review_reasons, (r) => r).map(([k, r]) => <p key={k}>• {r}</p>)}
        </div>
      )}
      {actions.length > 0 && (
        <div className="space-y-2.5" data-testid="capture-actions">
          <h2 className="font-mono text-[10px] tracking-[0.2em] uppercase c-muted">Acciones propuestas</h2>
          {actions.map((a) => (
            <ActionCard key={a.key} a={a} readOnly={!editable} result={cap.applied?.[a.key]} error={errors[a.key]} contactKeys={contactKeys}
              agreements={cap.agreements || []} isOwner={isOwner} onChange={(na) => setActions((s) => s.map((x) => (x.key === na.key ? na : x)))} />
          ))}
        </div>
      )}
      {Object.keys(cap.applied || {}).length > 0 && (
        <Section title="Resultado" testid="capture-results">
          <ul className="space-y-1">{Object.entries(cap.applied).map(([k, r]) => (
            <li key={k}><Link to={`${LINK[r.entity]}${r.id}`} className="text-sm c-accent hover:underline">{r.label}</Link></li>
          ))}</ul>
        </Section>
      )}
      {networkNeeds.map((a) => (
        <Link key={`net-${a.key}`} data-testid="capture-search-network-link" to={`/conecta/buscar?q=${encodeURIComponent(a.data.text)}`}
          className="c-card p-4 flex items-center gap-2 text-sm font-semibold c-accent"><Network className="w-4 h-4" /> Buscar en la red CONECTA: {a.data.text}</Link>
      ))}
      {cap.tipo_nota === "consulta" && (cap.transcription || cap.text_input) && (
        <Link data-testid="capture-answer-link" to={`/conecta/cruce?q=${encodeURIComponent(cap.transcription || cap.text_input)}`}
          className="c-card p-4 flex items-center gap-2 text-sm font-semibold c-accent"><MessageSquareText className="w-4 h-4" /> Es una pregunta: ver respuesta</Link>
      )}
      {editable && actions.length === 0 && <SaveAsNote captureId={cap.id} onSaved={setCap} />}
      {editable && (
        <div className={`grid gap-2 sticky bottom-24 ${actions.length ? "grid-cols-3" : "grid-cols-1"}`}>
          <button data-testid="capture-discard-btn" onClick={() => act("discard", "Descartada")} className="c-card py-3.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5"><Trash2 className="w-4 h-4" /> Descartar</button>
          {actions.length > 0 && <button data-testid="capture-confirm-btn" disabled={busy} onClick={apply} className="col-span-2 c-btn-primary py-3.5 flex items-center justify-center gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Confirmar todo</button>}
        </div>
      )}
    </div>
  );
}
